// The Assistant's approvals Help article (REQ-112, REQ-113, SCR-312): how a proposed change waits
// for a person, how to review it, the typed confirmations of a risky change, approving, rejecting
// with a reason, and what each refusal means. Plain words; no host names and no ids.
import { Callout, HelpLink, Related } from "./parts";

export function AssistantApprovals() {
  return (
    <>
      <p>
        The Assistant never changes anything by itself. When you ask it to change something, such as raising a quota or
        turning models off, it <strong>proposes</strong> the change and waits. Nothing is written until a person reviews
        the proposal and approves it.
      </p>

      <h2 id="how-it-works">How a change works</h2>
      <ol>
        <li>The Assistant reads what it would change and proposes the change. A card appears in the conversation.</li>
        <li>You review the card: what would change, and any risks it carries.</li>
        <li>You approve it, or reject it with a reason.</li>
        <li>
          On approval the change is made <strong>as you</strong>, with your own permissions at that moment, and recorded in
          the audit log as made via the Assistant on your behalf. Nexus then reads the setting back to verify the change took
          effect.
        </li>
      </ol>
      <p>
        A proposal waits for an hour. After that it expires, and the Assistant has to propose it again. Ending the
        conversation also ends its proposals: an ended conversation opens read-only, and its cards can no longer be approved
        or rejected.
      </p>

      <h2 id="reviewing">Reviewing a change</h2>
      <p>
        The card says what the change does in words, names what it changes (a team, a quota, a model), and lists each field
        with its value now and the value after. People are named by name or email. Choose <strong>Review</strong> to see the
        risks and the Approve and Reject buttons.
      </p>

      <h2 id="confirmations">Confirming a risky change</h2>
      <p>
        Some changes are risky: they delete something, turn models off, turn guardrails off, set a quota to zero, change who
        can administer, remove the last way to sign in, revoke keys, send requests to another provider, or change many
        things at once. The card lists each risk with a sentence about why, and asks you to type a confirmation for it:
      </p>
      <ul>
        <li>the <strong>name</strong> of what it changes, for example “Codex”;</li>
        <li>a <strong>count</strong> of how many things it touches, for example “12”;</li>
        <li>or the word <strong>CONFIRM</strong>.</li>
      </ul>
      <p>
        The word to type is shown under each box. Type it exactly, with the same capitals. Approve stays off until every box
        matches. The Assistant never fills these in: the risks and the words come from Nexus itself, not from the model.
      </p>
      <Callout>A risk never blocks a change. It asks you to slow down and say, in your own typing, that you mean it.</Callout>

      <h2 id="approving">Approving</h2>
      <p>
        Approve sends the change exactly as the card shows it. While it applies the card says so. When it is done the card
        shows <strong>Applied and verified</strong>, who approved it, and what changed. If Nexus could not reach you an
        answer, the card says <strong>That did not go through</strong> and offers <strong>Try again</strong>: approving the
        same change twice applies it once.
      </p>

      <h2 id="rejecting">Rejecting</h2>
      <p>
        Choose <strong>Reject</strong> and say in a sentence why. Nothing is changed. The Assistant reads your reason and can
        propose something else. A reason can be up to 500 characters; line breaks are sent as spaces.
      </p>

      <h2 id="when-it-fails">When approving fails</h2>
      <ul>
        <li>
          <strong>Changed since it was proposed.</strong> Someone changed the same thing after the Assistant proposed it, so
          nothing was written. Ask the Assistant to propose it again: it reads the new values first.
        </li>
        <li>
          <strong>This change was updated.</strong> The proposal is no longer the one on your screen. Reload it and review it
          again.
        </li>
        <li>
          <strong>This proposal expired.</strong> It waited more than an hour. Ask the Assistant to propose it again.
        </li>
        <li>
          <strong>Already decided.</strong> Someone else approved or rejected it first; the card shows what happened.
        </li>
        <li>
          <strong>Confirmation needed.</strong> A confirmation did not match. Type each one exactly as shown.
        </li>
        <li>
          <strong>This conversation has ended.</strong> A change in an ended conversation can no longer be approved, and
          nothing was written. Start a new conversation and ask again.
        </li>
        <li>
          <strong>Refused.</strong> The setting's own screen refused the change without saying why; nothing was written.
        </li>
        <li>
          <strong>Made, not verified.</strong> The change was made, but reading it back afterwards failed. Check the
          setting itself; don&apos;t ask for the change again.
        </li>
        <li>
          <strong>Not allowed</strong> or <strong>Not editable here.</strong> Your sign-in may not make this change, or AllCode
          manages it for the customer. Ask someone who can, or see{" "}
          <HelpLink to="signing-in-and-roles">Signing in and roles</HelpLink>.
        </li>
      </ul>

      <Related to={["assistant", "audit-log", "quotas-and-notifications"]} />
    </>
  );
}
